// Rendu du contenu Markdown de l'article

import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { sanitizeUrl } from '@/lib/security/urlSanitizer';

interface ArticleContentProps {
  content: string;
}

const ArticleContent = ({ content }: ArticleContentProps) => {
  return (
    <article className="max-w-3xl mx-auto prose prose-invert prose-lg">
      <ReactMarkdown
        components={{
          // Titres
          h2: ({ children }) => (
            <h2 className="text-2xl font-bold text-white mt-12 mb-4 first:mt-0">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-xl font-semibold text-white mt-8 mb-3">
              {children}
            </h3>
          ),
          // Paragraphes
          p: ({ children }) => (
            <p className="text-lg text-white/80 leading-relaxed mb-6">
              {children}
            </p>
          ),
          // Listes
          ul: ({ children }) => (
            <ul className="space-y-2 mb-6 ml-4">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="space-y-2 mb-6 ml-4 list-decimal">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-lg text-white/80 leading-relaxed pl-2 marker:text-[#F59E0B]">
              {children}
            </li>
          ),
          // Liens
          a: ({ href, children }) => {
            // Liens internes
            if (href?.startsWith('/')) {
              return (
                <Link
                  to={href}
                  className="text-[#F59E0B] hover:underline font-medium"
                >
                  {children}
                </Link>
              );
            }
            // Liens externes
            return (
              <a
                href={sanitizeUrl(href || '')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#F59E0B] hover:underline font-medium"
              >
                {children}
              </a>
            );
          },
          // Texte en gras
          strong: ({ children }) => (
            <strong className="font-semibold text-white">
              {children}
            </strong>
          ),
          // Texte en italique
          em: ({ children }) => (
            <em className="italic text-white/90">
              {children}
            </em>
          ),
          // Citations
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-[#F59E0B] bg-[#1E293B]/50 pl-6 pr-4 py-4 my-6 rounded-r-lg">
              {children}
            </blockquote>
          ),
          // Code inline
          code: ({ children }) => (
            <code className="px-1.5 py-0.5 bg-[#1E293B] rounded text-[#F59E0B] text-base font-mono">
              {children}
            </code>
          ),
          // Tableaux
          table: ({ children }) => (
            <div className="overflow-x-auto my-6">
              <table className="min-w-full border-collapse">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-[#1E293B]/50">
              {children}
            </thead>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3 text-left text-sm font-semibold text-white border-b border-white/10">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3 text-sm text-white/70 border-b border-white/5">
              {children}
            </td>
          ),
          // Separateur horizontal
          hr: () => (
            <hr className="my-8 border-white/10" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </article>
  );
};

export default ArticleContent;
